import { useState } from 'react';

import DeleteModal from '@atom/components/common/DeleteModal';
import RenameModal from '@atom/components/common/RenameModal';
import { Icon, IconButton } from '@atom/mui';
import {
  ArrowBackIcon,
  DeleteIcon,
  EditIcon,
  FileDownloadIcon,
  InfoIcon,
  StarIcon,
} from '@atom/mui/icons';
import colors from '@atom/styles/colors';
import { Media } from '@atom/types/media';
import { mediaFileName, mediaUrl } from '@atom/utilities/mediaUtilities';
import textDisplayUtilities from '@atom/utilities/textDisplayUtilities';

import classes from './mediaLightbox.module.css';

export type MediaLightboxToolbarProps = {
  media: Media;
  isCoverPhoto: boolean;
  canUpdateMedia: boolean;
  canDeleteMedia: boolean;
  canMarkAsCoverPhoto: boolean;
  onClose: () => void;
  onRename: (mediaId: string, name: string) => void;
  onDelete: (mediaId: string) => void;
  onCoverPhotoToggle: (mediaId: string) => void;
  onDetailsToggle: () => void;
};

const MediaLightboxToolbar = ({
  media,
  isCoverPhoto,
  canUpdateMedia,
  canDeleteMedia,
  canMarkAsCoverPhoto,
  onClose,
  onRename,
  onDelete,
  onCoverPhotoToggle,
  onDetailsToggle,
}: MediaLightboxToolbarProps) => {
  const [renameOpen, setRenameOpen] = useState<boolean>(false);
  const [deleteOpen, setDeleteOpen] = useState<boolean>(false);

  const handleDelete = () => {
    setDeleteOpen(false);
    onDelete(media.id);
  };

  const coverPhotoText = isCoverPhoto
    ? 'Unmark as Cover Photo'
    : 'Mark as Cover Photo';
  const coverPhotoColor = isCoverPhoto
    ? colors.brand.blue
    : colors.neutral.white;

  const fileName = mediaFileName(media);
  const mediaTypeLabel = textDisplayUtilities.capitalize(media.type);

  return (
    <div className={classes.toolbar} data-cy="mediaLightboxToolbar">
      <div className={classes.toolbarGroup}>
        <IconButton onClick={onClose} data-cy="mediaLightboxBackButton">
          <Icon color={colors.neutral.white} icon={ArrowBackIcon} />
        </IconButton>
        <div className={classes.fileName}>{fileName}</div>
      </div>
      <div className={classes.toolbarGroup}>
        {canUpdateMedia && (
          <>
            <IconButton
              onClick={() => setRenameOpen(true)}
              tooltip="Rename"
              data-cy="mediaLightboxRenameButton"
            >
              <Icon color={colors.neutral.white} icon={EditIcon} />
            </IconButton>
            {/* Arrow keys in the name field would otherwise page the
                carousel behind the modal. */}
            <div onKeyDown={event => event.stopPropagation()}>
              <RenameModal
                id={media.id}
                name={media.name || ''}
                type={media.type}
                renameAction={onRename}
                open={renameOpen}
                closeModal={() => setRenameOpen(false)}
              />
            </div>
          </>
        )}
        <a href={mediaUrl(media.fileId)} download={fileName}>
          <IconButton tooltip="Download" data-cy="mediaLightboxDownloadButton">
            <Icon color={colors.neutral.white} icon={FileDownloadIcon} />
          </IconButton>
        </a>
        {canMarkAsCoverPhoto && (
          <IconButton
            onClick={() => onCoverPhotoToggle(media.id)}
            tooltip={coverPhotoText}
            data-cy="mediaLightboxCoverPhotoButton"
          >
            <Icon color={coverPhotoColor} icon={StarIcon} />
          </IconButton>
        )}
        {canDeleteMedia && (
          <>
            <IconButton
              onClick={() => setDeleteOpen(true)}
              tooltip="Delete"
              data-cy="mediaLightboxDeleteButton"
            >
              <Icon color={colors.neutral.white} icon={DeleteIcon} />
            </IconButton>
            <DeleteModal
              title={`Delete ${mediaTypeLabel}`}
              open={deleteOpen}
              onCancel={() => setDeleteOpen(false)}
              onConfirm={handleDelete}
              confirmText="Delete"
              content={`Are you sure you want to delete this ${media.type}?`}
            />
          </>
        )}
        <IconButton
          onClick={onDetailsToggle}
          tooltip="Information"
          data-cy="mediaLightboxDetailsButton"
        >
          <Icon color={colors.neutral.white} icon={InfoIcon} />
        </IconButton>
      </div>
    </div>
  );
};

export default MediaLightboxToolbar;
